import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getDefaultSenderIdentity, UserSenderIdentity } from '@/services/userSenderIdentitiesService';
import { useAuth } from '@/context/AuthContext';

interface UseDefaultSenderReturn {
  defaultSender: UserSenderIdentity | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function useDefaultSender(): UseDefaultSenderReturn {
  const { user } = useAuth();
  const [defaultSender, setDefaultSender] = useState<UserSenderIdentity | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDefaultSender = async () => {
    if (!user?.id) {
      setDefaultSender(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const sender = await getDefaultSenderIdentity(user.id);
      setDefaultSender(sender);
    } catch (error) {
      console.error('Error fetching default sender:', error);
      setDefaultSender(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaultSender();

    // Subscribe to real-time changes on user_sender_identities table
    if (!user?.id) return;

    const channel = supabase
      .channel('default-sender-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_sender_identities',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Refresh when any sender identity changes (especially is_default or verification_status)
          fetchDefaultSender();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return {
    defaultSender,
    isLoading,
    refresh: fetchDefaultSender,
  };
}
